import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabaseClient'

interface CompanyAddress {
  street?: string
  city?: string
  province?: string
  postal_code?: string
  country?: string
}

interface Company {
  id: string
  name: string
  slug: string
  email: string
  phone: string | null
  address: CompanyAddress
  subscription_tier: 'solo' | 'team' | 'business' | 'enterprise'
  subscription_status: 'active' | 'expired' | 'cancelled'
  subscription_expires_at?: string
  features: Record<string, boolean>
  created_at: string
  onboarding_complete: boolean
}

interface CompanyContextType {
  currentCompany: Company | null
  currentCompanyId: string | null
  isLoading: boolean
  error: string | null
  refetchCompany: () => Promise<void>
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined)

/**
 * Extract company slug from subdomain or URL parameter
 * Priority order:
 *   1. URL parameter (?company=slug)
 *   2. Subdomain (sk-window-cleaning.sergio.com)
 *   3. localStorage (development only)
 * Examples:
 *   - ?company=sk-window-cleaning → sk-window-cleaning
 *   - sk-window-cleaning.sergio.com → sk-window-cleaning
 *   - app.sergio.com → null (main app domain)
 *   - localhost:3000 → null (development, use fallback)
 */
export function getCompanySlugFromSubdomain(): string | null {
  // Check URL parameter first (highest priority)
  const urlParams = new URLSearchParams(window.location.search)
  const urlCompany = urlParams.get('company')
  if (urlCompany) {
    // Also save to localStorage for development convenience
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      localStorage.setItem('dev_company_slug', urlCompany)
    }
    return urlCompany
  }

  const hostname = window.location.hostname

  // Development fallback - check localStorage for testing
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const devSlug = localStorage.getItem('dev_company_slug')
    return devSlug || null
  }

  const parts = hostname.split('.')

  // Need at least 3 parts for subdomain (subdomain.domain.tld)
  if (parts.length < 3) {
    return null
  }

  const subdomain = parts[0]

  // Skip main app domains
  if (subdomain === 'app' || subdomain === 'www') {
    return null
  }

  return subdomain
}

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastCheckedSlug, setLastCheckedSlug] = useState<string | null>(null)
  const initialLoadCompleteRef = React.useRef(false)

  const fetchCompany = React.useCallback(async (isBackgroundRefetch = false) => {
    try {
      // Only show loading state for initial load, not background refetches
      if (!isBackgroundRefetch) {
        setIsLoading(true)
      }
      setError(null)

      const slug = getCompanySlugFromSubdomain()

      // Update last checked slug
      setLastCheckedSlug(slug)

      if (!slug) {
        setCurrentCompany(null)
        setIsLoading(false)
        return
      }

      // IMPORTANT: Check auth FIRST before fetching company (RLS requires auth.uid())
      // Add timeout to prevent hanging forever
      const authPromise = supabase.auth.getUser()
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Auth check timeout')), 10000)
      )

      let user, authError
      try {
        const result = await Promise.race([authPromise, timeoutPromise])
        user = result.data.user
        authError = result.error
      } catch (err: any) {
        // On timeout, try to get session from storage
        const { data: { session } } = await supabase.auth.getSession()
        user = session?.user ?? null
      }

      if (authError || !user) {
        setCurrentCompany(null)
        setIsLoading(false)
        return
      }

      // Fetch company by slug - first get the company, then verify user access
      const { data: companyData, error: fetchError } = await supabase
        .from('companies')
        .select('*')
        .eq('slug', slug)
        .in('subscription_status', ['active', 'expired', 'cancelled'])
        .single()

      const company = companyData as Company | undefined

      if (fetchError) {
        setError('Failed to load company information')
        setCurrentCompany(null)
        setIsLoading(false)
        return
      }

      if (!company) {
        setError(`Company "${slug}" not found or inactive`)
        setCurrentCompany(null)
        setIsLoading(false)
        return
      }

      // SECURITY: Verify user has access to this company (additional check beyond RLS)
      // Check if user is super admin (god account)
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('super_admin')
        .eq('id', user.id)
        .maybeSingle()

      if (userError) {
        console.error('Error fetching user super_admin status:', userError)
      }

      const isSuperAdmin = userData?.super_admin ?? false

      // Super admins can access any company, skip membership check
      if (!isSuperAdmin) {
        const { data: membership } = await supabase
          .from('user_companies')
          .select('role, is_active')
          .eq('company_id', company.id)
          .eq('user_id', user.id)
          .eq('is_active', true)
          .single()

        if (!membership) {
          setError('You do not have access to this company')
          setCurrentCompany(null)
          setIsLoading(false)
          return
        }
      }

      setCurrentCompany(company)
      setIsLoading(false)
      initialLoadCompleteRef.current = true
    } catch (err) {
      console.error('Company fetch error:', err)
      setError('An unexpected error occurred')
      setCurrentCompany(null)
      setIsLoading(false)
    }
  }, []) // Properly memoized

  // SECURITY FIX P0-6: Fetch company on mount and when subdomain changes
  useEffect(() => {
    fetchCompany()

    // Listen for auth state changes (login/logout) and refetch company
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        // Only fetch on first SIGNED_IN, ignore duplicate events
        if (!initialLoadCompleteRef.current) {
          fetchCompany(false)
        }
      } else if (event === 'TOKEN_REFRESHED') {
        // Token refreshed in background, do silent refetch
        fetchCompany(true)
      } else if (event === 'SIGNED_OUT') {
        // User logged out, clear company
        setCurrentCompany(null)
        initialLoadCompleteRef.current = false
      }
    })

    return () => {
      // Cleanup subscription
      subscription.unsubscribe()
    }
  }, [fetchCompany])

  // SECURITY FIX P0-6: Monitor for subdomain and URL parameter changes
  useEffect(() => {
    const checkSubdomainChange = () => {
      const currentSlug = getCompanySlugFromSubdomain()

      // Only refetch if slug actually changed from what we last checked
      if (currentSlug !== lastCheckedSlug) {
        // Treat subdomain changes as initial load since it's a different company
        fetchCompany(false)
      }
    }

    // Check every 2 seconds for subdomain changes (reduced frequency)
    const interval = setInterval(checkSubdomainChange, 2000)

    return () => clearInterval(interval)
  }, [lastCheckedSlug, fetchCompany])

  const value: CompanyContextType = {
    currentCompany,
    currentCompanyId: currentCompany?.id || null,
    isLoading,
    error,
    refetchCompany: fetchCompany,
  }

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
}

/**
 * Hook to access company context
 *
 * @example
 * const { currentCompany, currentCompanyId, isLoading } = useCompany()
 *
 * if (isLoading) return <Loading />
 * if (!currentCompany) return <OnboardingModal />
 *
 * // Use currentCompanyId in API calls
 * const { data } = await supabase
 *   .from('clients')
 *   .select('*')
 *   .eq('company_id', currentCompanyId)
 */
export function useCompany() {
  const context = useContext(CompanyContext)

  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider')
  }

  return context
}

/**
 * Development helper: Set company slug for local testing
 *
 * @example
 * // In browser console:
 * localStorage.setItem('dev_company_slug', 'sk-window-cleaning')
 * window.location.reload()
 */
export function setDevCompanySlug(slug: string) {
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    console.warn('setDevCompanySlug only works in development')
    return
  }

  localStorage.setItem('dev_company_slug', slug)
  console.log(`Development company slug set to: ${slug}`)
  console.log('Reload the page to apply changes')
}

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).setDevCompanySlug = setDevCompanySlug
}
